/* eslint-disable indent */
import React, { memo } from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { Field, FieldArray } from 'formik';
import get from 'lodash/get';
import { makeStyles } from '@material-ui/styles';
import { set } from 'lodash';
import { useTranslation } from '../../../../src/i18n';
import TitleWithInfo from '../../../../components/TitleWithInfo';
import FormikCheckbox from '../../../../components/Fields/CheckboxField/FormikCheckbox';
import FormikTextField from '../../../../components/Fields/TextField';
import AddAnotherField from '../../../../components/AddAnotherField';
import ComposerWorkFields from '../../../../components/EditComponents/ComposerWorkFields';
import DisclaimerBox from '../../../../components/EditComponents/DisclaimerBox';
import { TP } from '../../../../constants/consts';

const useStyles = makeStyles(theme => ({
  doubleBillWorks: {
    display: 'flex',
    maxWidth: 'unset',
    justifyContent: 'space-between',
    [theme.breakpoints.down('xs')]: {
      display: 'block',
    },
  },
}));

const MusicalWorkComposerEditFields = ({
  values,
  composerField,
  workField,
  doubleBillField,
  composers,
  works,
  searchWorkFilters,
  searchComposersFilters,
  resetMusicWorksOptions,
  resetOptions,
  loadingValue,
  setValues,
  disabled,
  onWorkChange,
  nameField,
  setFieldValue,
  doubleWorks,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();

  return (
    <Grid container spacing={2} style={{ maxWidth: 2000 }}>
      <Grid item>
        <DisclaimerBox text={t(`${TP}.FN_CONCERT_PRODUCTION_TYPE_DISCLAIMER`)} />
      </Grid>
      <ComposerWorkFields
        values={values}
        composerField={composerField}
        workField={workField}
        composers={composers}
        works={works}
        searchWorkFilters={searchWorkFilters}
        searchComposersFilters={searchComposersFilters}
        resetMusicWorksOptions={resetMusicWorksOptions}
        resetComposerOptions={resetOptions}
        loadingValue={loadingValue}
        setValues={setValues}
        disabled={disabled}
        onWorkChange={v => {
          onWorkChange(v);
          const updatedValues = { ...values };
          set(updatedValues, workField?.name, v);
          setValues(updatedValues);
        }}
        composerGridParams={{ md: 6, sm: 6, xs: 12 }}
        workGridParams={{ md: 6, sm: 6, xs: 12 }}
        setFieldValue={setFieldValue}
        preventWorkSetValues
      />
      <Grid item sm={6} xs={12}>
        <Field name={nameField.name} label={t(nameField.label)} component={FormikTextField} />
      </Grid>
      {doubleBillField && (
        <Grid item sm={12} xs={12}>
          <Field
            name={doubleBillField.name}
            label={<TitleWithInfo title={t(doubleBillField.label)} tooltip={t(doubleBillField.tooltip)} />}
            component={FormikCheckbox}
          />
        </Grid>
      )}
      <Grid item sm={10} xs={12}>
        {values[doubleBillField?.name] && (
          <FieldArray name={doubleWorks.name}>
            {arrayHelpers =>
              values[doubleWorks.name].map((composerMwGroup, index) => (
                <AddAnotherField
                  key={`${values[doubleWorks.name]}.${index}`}
                  values={values[doubleWorks.name]}
                  removeFunc={() => arrayHelpers.remove(index)}
                  addFunc={() =>
                    arrayHelpers.push({
                      composer: '',
                      work: '',
                    })
                  }
                  isLast={index === values[doubleWorks.name].length - 1}
                  addDisabled={!get(composerMwGroup, 'work.value')}
                  inputClassName={classes.doubleBillWorks}
                  addButtonPosition="bottom"
                >
                  <ComposerWorkFields
                    values={values}
                    composerField={{ ...composerField, name: `${doubleWorks.name}.${index}.composer` }}
                    workField={{ ...workField, name: `${doubleWorks.name}.${index}.work` }}
                    composers={composers}
                    works={works}
                    searchWorkFilters={searchWorkFilters}
                    searchComposersFilters={searchComposersFilters}
                    resetMusicWorksOptions={resetMusicWorksOptions}
                    resetComposerOptions={resetOptions}
                    loadingValue={loadingValue}
                    setValues={setValues}
                    setFieldValue={setFieldValue}
                    composerGridParams={{ md: 5, sm: 5, xs: 12 }}
                    workGridParams={{ md: 6, sm: 6, xs: 12 }}
                  />
                </AddAnotherField>
              ))
            }
          </FieldArray>
        )}
      </Grid>
    </Grid>
  );
};

MusicalWorkComposerEditFields.propTypes = {
  composerField: PropTypes.object.isRequired,
  workField: PropTypes.object.isRequired,
  nameField: PropTypes.object,
  doubleBillField: PropTypes.object,
  composers: PropTypes.array.isRequired,
  works: PropTypes.array.isRequired,
  searchWorkFilters: PropTypes.func.isRequired,
  searchComposersFilters: PropTypes.func.isRequired,
  resetOptions: PropTypes.func.isRequired,
  loadingValue: PropTypes.string,
  setFieldValue: PropTypes.func.isRequired,
  setValues: PropTypes.func.isRequired,
  resetMusicWorksOptions: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  onWorkChange: PropTypes.func,
};

MusicalWorkComposerEditFields.defaultProps = {
  disabled: false,
  onWorkChange: () => {},
  nameField: null,
};

export default memo(MusicalWorkComposerEditFields);
